/**
 * Ported VERBATIM from `apr_template/src/components/QAAssessmentModal.tsx`.
 * Only import paths change. See ../compat/ for the shims.
 */
import React, { useState, useEffect } from 'react';
import apiService, { authFetch } from '../compat/api';
import './QAAssessmentModal.css';

export interface QAQuestion {
  questionId: string;
  questionText: string;
  dimensionId: string;
  dimensionName: string;
  componentId: string;
  componentName: string;
  componentWeight: number;
  answerOptions: AnswerOption[];
  selectedAnswerId?: string;
  selectedScore?: number;
  assessmentStatus: string;
}

export interface AnswerOption {
  id: string;
  text: string;
  score: number;
  displayOrder: number;
}

export interface AssessmentResults {
  applicationId: string;
  totalQuestions: number;
  answeredQuestions: number;
  overallScore: number;
  completionPercentage: number;
  recommendation: string;
  dimensionScores: DimensionScore[];
}

export interface DimensionScore {
  id: string;
  name: string;
  score: number;
  questionsAnswered: number;
  totalQuestions: number;
}

interface QAAssessmentModalProps {
  applicationId: string;
  applicationName: string;
  aprRunId?: string;
  isOpen: boolean;
  onClose: () => void;
  onComplete: (results: AssessmentResults) => void;
}

const QAAssessmentModal: React.FC<QAAssessmentModalProps> = ({
  applicationId,
  applicationName,
  aprRunId,
  isOpen,
  onClose,
  onComplete
}) => {
  const [questions, setQuestions] = useState<QAQuestion[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedTab, setSelectedTab] = useState('all');
  const [answers, setAnswers] = useState<Record<string, { answerId: string; score: number }>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [scores, setScores] = useState<AssessmentResults | null>(null);
  const [dimensions, setDimensions] = useState<string[]>([]);

  useEffect(() => {
    if (isOpen && applicationId) {
      loadAssessmentData();
    }
  }, [isOpen, applicationId, aprRunId]);

  const loadAssessmentData = async () => {
    setLoading(true);
    try {
      // Load questions from qa_assessments table
      console.log('Loading QA data for application:', applicationId, 'aprRunId:', aprRunId);
      const response = await authFetch(`/api/assessments/qa/${applicationId}${aprRunId ? `?apr_run_id=${aprRunId}` : ''}`);
      if (!response.ok) throw new Error('Failed to load questions');
      
      const qaData = await response.json();
      console.log('QA Data loaded:', qaData.length, 'records');
      
      // Transform qa_assessments data into questions format
      const questionsMap: Record<string, QAQuestion> = {};
      const answersMap: Record<string, { answerId: string; score: number }> = {};
      const dimensionsSet = new Set<string>();

      for (const qa of qaData) {
        dimensionsSet.add(qa.dimension_name);
        
        if (!questionsMap[qa.question_id]) {
          // Get answer options for this question
          try {
            console.log('Loading answer options for question:', qa.question_id);
            const optionsResponse = await authFetch(`/api/questions/${qa.question_id}/answer-options`);
            const rawAnswerOptions = optionsResponse.ok ? await optionsResponse.json() : [];

            // Transform snake_case to camelCase for answer options
            const answerOptions = rawAnswerOptions.map((opt: any) => ({
              id: opt.id,
              text: opt.text,
              score: opt.score,
              displayOrder: opt.display_order || opt.displayOrder || 0
            }));

            console.log('Answer options loaded for', qa.question_id, ':', answerOptions.length, 'options');

            // Check if assessment is completed (case-insensitive)
            const isCompleted = qa.assessment_status && qa.assessment_status.toLowerCase() === 'completed';

            console.log('Question assessment status:', {
              questionId: qa.question_id,
              rawStatus: qa.assessment_status,
              isCompleted,
              answerOptionId: qa.answer_option_id,
              answerOptionsIds: answerOptions.map((o: any) => o.id)
            });

            questionsMap[qa.question_id] = {
              questionId: qa.question_id,
              questionText: qa.question_text,
              dimensionId: qa.dimension_id,
              dimensionName: qa.dimension_name,
              componentId: qa.component_id,
              componentName: qa.component_name,
              componentWeight: qa.component_weight,
              answerOptions: answerOptions || [], // Ensure it's always an array
              selectedAnswerId: isCompleted ? qa.answer_option_id : undefined,
              selectedScore: isCompleted ? qa.answer_score : undefined,
              assessmentStatus: qa.assessment_status
            };
          } catch (error) {
            console.error(`Failed to load answer options for question ${qa.question_id}:`, error);
            // Create question with empty answer options as fallback
            const isCompleted = qa.assessment_status && qa.assessment_status.toLowerCase() === 'completed';
            questionsMap[qa.question_id] = {
              questionId: qa.question_id,
              questionText: qa.question_text,
              dimensionId: qa.dimension_id,
              dimensionName: qa.dimension_name,
              componentId: qa.component_id,
              componentName: qa.component_name,
              componentWeight: qa.component_weight,
              answerOptions: [], // Empty fallback
              selectedAnswerId: isCompleted ? qa.answer_option_id : undefined,
              selectedScore: isCompleted ? qa.answer_score : undefined,
              assessmentStatus: qa.assessment_status
            };
          }

          // Also populate answersMap for completed questions (case-insensitive)
          if (qa.assessment_status && qa.assessment_status.toLowerCase() === 'completed') {
            answersMap[qa.question_id] = {
              answerId: qa.answer_option_id,
              score: qa.answer_score
            };
          }
        }
      }

      // Define the desired dimension order
      const dimensionOrder = [
        'Business Value & Strategic Fit',
        'Business Value',
        'Strategic Fit',
        'Business Case',
        'Application Performance',
        'Performance',
        'Application Risk Mitigation',
        'Risk Mitigation',
        'Risk',
        'Architectural Maturity',
        'Architecture'
      ];

      // Sort questions by specified dimension order, then component, then question
      const questionsList = Object.values(questionsMap).sort((a, b) => {
        // Get dimension order positions
        const getOrderPos = (dimName: string) => {
          const pos = dimensionOrder.findIndex(d => dimName.includes(d) || d.includes(dimName));
          return pos === -1 ? 999 : pos;
        };

        const dimOrderA = getOrderPos(a.dimensionName);
        const dimOrderB = getOrderPos(b.dimensionName);

        if (dimOrderA !== dimOrderB) return dimOrderA - dimOrderB;

        const compCompare = a.componentName.localeCompare(b.componentName);
        if (compCompare !== 0) return compCompare;
        return a.questionText.localeCompare(b.questionText);
      });

      setQuestions(questionsList);
      setAnswers(answersMap);
      
      // Sort dimensions in the same order as questions
      const sortedDimensions = Array.from(dimensionsSet).sort((a, b) => {
        const getOrderPos = (dimName: string) => {
          const pos = dimensionOrder.findIndex(d => dimName.includes(d) || d.includes(dimName));
          return pos === -1 ? 999 : pos;
        };
        return getOrderPos(a) - getOrderPos(b);
      });
      
      setDimensions(sortedDimensions);
      
      // Load current scores
      await loadScores();
      
    } catch (error) {
      console.error('Error loading assessment data:', error);
    }
    setLoading(false);
  };

  const loadScores = async () => {
    try {
      const response = await authFetch(`/api/assessments/application/${applicationId}/scores${aprRunId ? `?apr_run_id=${aprRunId}` : ''}`);
      if (response.ok) {
        const scoresData = await response.json();
        setScores(scoresData);
      }
    } catch (error) {
      console.error('Error loading scores:', error);
    }
  };

  const triggerRescoring = async () => {
    try {
      console.log('Triggering rescoring for application:', applicationId);
      await apiService.rescoreApplication(applicationId, aprRunId);
      console.log('Rescoring completed successfully');
      
      // Reload scores after rescoring
      await loadScores();
    } catch (error) {
      console.error('Error during rescoring:', error);
    }
  };

  const handleAnswerSelect = async (questionId: string, answerId: string, score: number) => {
    setSaving(true);
    try {
      // Find the question to get its component weight
      const question = questions.find(q => q.questionId === questionId);
      const weight = question?.componentWeight || 1;
      
      // Update local state
      setAnswers(prev => ({
        ...prev,
        [questionId]: { answerId, score }
      }));

      // Update question status
      setQuestions(prev => prev.map(q => 
        q.questionId === questionId 
          ? { ...q, selectedAnswerId: answerId, selectedScore: score, assessmentStatus: 'Completed' }
          : q
      ));

      // Save to database with score and weight
      await apiService.saveAssessmentResponse({
        application_id: applicationId,
        question_id: questionId,
        answer_option_id: answerId,
        apr_run_id: aprRunId,
        score: score,
        weight: weight
      });

      // Reload scores after saving
      await loadScores();

    } catch (error) {
      console.error('Error saving answer:', error);
    }
    setSaving(false);
  };

  const getFilteredQuestions = () => {
    if (selectedTab === 'all') return questions;
    return questions.filter(q => q.dimensionName === selectedTab);
  };

  const getCompletionStats = () => {
    const answered = questions.filter(q => q.assessmentStatus && q.assessmentStatus.toLowerCase() === 'completed').length;
    const total = questions.length;
    const percentage = total > 0 ? (answered / total) * 100 : 0;
    return { answered, total, percentage };
  };

  const filteredQuestions = getFilteredQuestions();
  const currentQuestion = filteredQuestions[currentQuestionIndex];
  const stats = getCompletionStats();

  // Debug logging
  useEffect(() => {
    console.log('QA Modal Debug Info:', {
      questionsCount: questions.length,
      filteredQuestionsCount: filteredQuestions.length,
      currentQuestionIndex,
      currentQuestion: currentQuestion ? {
        id: currentQuestion.questionId,
        text: currentQuestion.questionText,
        hasAnswerOptions: !!currentQuestion.answerOptions,
        answerOptionsCount: currentQuestion.answerOptions?.length || 0,
        selectedAnswerId: currentQuestion.selectedAnswerId,
        assessmentStatus: currentQuestion.assessmentStatus,
        answerOptionIds: currentQuestion.answerOptions?.map(o => o.id) || []
      } : null
    });

    // Extra debug for answer matching
    if (currentQuestion && currentQuestion.selectedAnswerId && currentQuestion.answerOptions) {
      const matchFound = currentQuestion.answerOptions.some(o => o.id === currentQuestion.selectedAnswerId);
      console.log('Answer ID Match Check:', {
        selectedAnswerId: currentQuestion.selectedAnswerId,
        selectedAnswerIdType: typeof currentQuestion.selectedAnswerId,
        optionIds: currentQuestion.answerOptions.map(o => ({ id: o.id, type: typeof o.id })),
        matchFound
      });
    }
  }, [questions, filteredQuestions, currentQuestionIndex, currentQuestion]);

  if (!isOpen) return null;

  if (loading) {
    return (
      <div className="md-dialog md-dialog--open">
        <div className="md-dialog__backdrop" onClick={onClose}></div>
        <div className="md-dialog__container">
          <div className="md-dialog__content">
            <div className="loading-spinner">Loading Q&A Assessment...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: '12px',
          padding: '0',
          width: '90%',
          maxWidth: '900px',
          maxHeight: '90vh',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1), 0 8px 24px rgba(0,0,0,0.15)',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        {/* Header */}
        <div style={{
          padding: '20px 24px',
          borderBottom: '1px solid #e0e0e0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start'
        }}>
          <div>
            <h2 style={{
              margin: '0 0 8px 0',
              fontSize: '24px',
              fontWeight: '500',
              color: '#1a1a1a'
            }}>
              Application Assessment
            </h2>
            <p style={{
              margin: 0,
              fontSize: '14px',
              color: '#666'
            }}>
              {applicationName}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#666',
              padding: '4px 8px',
              borderRadius: '4px'
            }}
            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
            onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            ×
          </button>
        </div>

        {/* Progress Indicator */}
        <div style={{
          padding: '16px 24px',
          backgroundColor: '#f8f9fa',
          borderBottom: '1px solid #e0e0e0'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '8px'
          }}>
            <span style={{ fontSize: '12px', fontWeight: '500', textTransform: 'uppercase', color: '#666' }}>Progress</span>
            <span style={{ fontSize: '14px', color: '#333' }}>{stats.answered} of {stats.total} questions</span>
            <span style={{ fontSize: '14px', fontWeight: '500', color: '#1976d2' }}>{stats.percentage.toFixed(0)}%</span>
          </div>
          <div style={{
            height: '4px',
            backgroundColor: '#e0e0e0',
            borderRadius: '2px',
            overflow: 'hidden'
          }}>
            <div style={{
              height: '100%',
              backgroundColor: '#1976d2',
              borderRadius: '2px',
              width: `${stats.percentage}%`,
              transition: 'width 0.3s ease'
            }}></div>
          </div>
          {saving && <div style={{ marginTop: '8px', fontSize: '12px', color: '#f57c00', fontWeight: '500' }}>Saving...</div>}
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex',
          gap: '4px',
          padding: '12px 24px',
          borderBottom: '1px solid #e0e0e0',
          overflowX: 'auto',
          backgroundColor: 'white'
        }}>
          <button
            onClick={() => { setSelectedTab('all'); setCurrentQuestionIndex(0); }}
            style={{
              padding: '8px 16px',
              border: 'none',
              borderRadius: 0,
              fontSize: '13px',
              fontWeight: '500',
              cursor: 'pointer',
              backgroundColor: selectedTab === 'all' ? '#1976d2' : '#f0f0f0',
              color: selectedTab === 'all' ? 'white' : '#666',
              whiteSpace: 'nowrap',
              transition: 'all 0.2s'
            }}
          >
            All Dimensions
          </button>
          {dimensions.map(dim => (
            <button
              key={dim}
              onClick={() => { setSelectedTab(dim); setCurrentQuestionIndex(0); }}
              style={{
                padding: '8px 16px',
                border: 'none',
                borderRadius: 0,
                fontSize: '13px',
                fontWeight: '500',
                cursor: 'pointer',
                backgroundColor: selectedTab === dim ? '#1976d2' : '#f0f0f0',
                color: selectedTab === dim ? 'white' : '#666',
                whiteSpace: 'nowrap',
                transition: 'all 0.2s'
              }}
            >
              {dim}
            </button>
          ))}
        </div>

        {/* Content */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '24px',
          backgroundColor: '#fafafa'
        }}>
          {currentQuestion && currentQuestion.answerOptions ? (
            <div style={{ maxWidth: '700px', margin: '0 auto' }}>
              {/* Breadcrumb */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '12px',
                fontSize: '12px',
                color: '#666'
              }}>
                <span>{currentQuestion.dimensionName}</span>
                <span style={{ margin: '0 8px', color: '#999' }}>›</span>
                <span style={{ color: '#333', fontWeight: '500' }}>{currentQuestion.componentName}</span>
                <span style={{ margin: '0 8px', color: '#999' }}>›</span>
                {/* The component's weight in the score.
                    Square corners, a rule in its own colour and a wash behind it — the
                    same box every chip in the Evaluation tables uses. It was the last
                    oval left in this modal, sitting directly under a screen where
                    nothing else is one. */}
                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  height: '20px',
                  padding: '0 8px',
                  color: '#202124',
                  border: '1px solid #202124',
                  backgroundColor: '#20212424',
                  borderRadius: 0,
                  fontSize: '11px',
                  fontWeight: '600',
                  whiteSpace: 'nowrap'
                }}>
                  Weight: {currentQuestion.componentWeight ?? '-'}
                </span>
              </div>

              {/* Question Card */}
              <div style={{
                background: 'white',
                borderRadius: 0,
                padding: '20px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
                border: '1px solid #e0e0e0'
              }}>
                <div style={{ marginBottom: '12px' }}>
                  {/* Matched to the weight badge above it. Left as an oval it would
                      have been the only rounded thing in the card. */}
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    height: '22px',
                    padding: '0 10px',
                    color: '#0d47a1',
                    border: '1px solid #0d47a1',
                    backgroundColor: '#0d47a124',
                    borderRadius: 0,
                    fontSize: '11.5px',
                    fontWeight: '600',
                    whiteSpace: 'nowrap'
                  }}>
                    Question {currentQuestionIndex + 1}
                  </span>
                </div>
                <h3 style={{
                  margin: '0 0 16px 0',
                  fontSize: '16px',
                  fontWeight: '400',
                  color: '#333',
                  lineHeight: '1.4'
                }}>
                  {currentQuestion.questionText}
                </h3>

                {/* Answer Options */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {(currentQuestion.answerOptions || [])
                    .sort((a, b) => a.displayOrder - b.displayOrder)
                    .map(option => (
                      <label
                        key={option.id}
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          padding: '12px',
                          borderRadius: 0,
                          cursor: 'pointer',
                          backgroundColor: currentQuestion.selectedAnswerId === option.id ? '#e3f2fd' : 'transparent',
                          border: currentQuestion.selectedAnswerId === option.id ? '2px solid #1976d2' : '2px solid transparent',
                          transition: 'all 0.2s'
                        }}
                        onMouseOver={(e) => {
                          if (currentQuestion.selectedAnswerId !== option.id) {
                            e.currentTarget.style.backgroundColor = '#f5f5f5';
                          }
                        }}
                        onMouseOut={(e) => {
                          if (currentQuestion.selectedAnswerId !== option.id) {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }
                        }}
                      >
                        <input
                          type="radio"
                          name={`question-${currentQuestion.questionId}`}
                          value={option.id}
                          checked={currentQuestion.selectedAnswerId === option.id}
                          onChange={() => handleAnswerSelect(currentQuestion.questionId, option.id, option.score)}
                          style={{ marginRight: '12px', marginTop: '2px' }}
                        />
                        <span style={{ fontSize: '14px', color: '#333', lineHeight: '1.4' }}>
                          {option.text}
                          {currentQuestion.selectedAnswerId === option.id && (
                            <span style={{
                              marginLeft: '8px',
                              padding: '2px 8px',
                              backgroundColor: '#1976d2',
                              color: 'white',
                              borderRadius: 0,
                              fontSize: '11px',
                              fontWeight: '500'
                            }}>
                              Selected
                            </span>
                          )}
                        </span>
                      </label>
                    ))}
                </div>
              </div>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '48px 24px', color: '#666' }}>
              {!currentQuestion
                ? (selectedTab === 'all'
                    ? 'No questions available for this application.'
                    : `No questions available for ${selectedTab} dimension.`
                  )
                : 'Loading answer options for this question...'
              }
              {currentQuestion && !currentQuestion.answerOptions && (
                <div style={{ marginTop: '1rem', fontSize: '0.875rem', color: '#dc3545' }}>
                  Failed to load answer options for question: {currentQuestion.questionText}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 24px',
          borderTop: '1px solid #e0e0e0',
          backgroundColor: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          {/* Question paging.
              Moving between questions is the single most-used control in this modal —
              forty times per application — but it was set as borderless text at the same
              weight as everything else in the footer, so it read as a caption rather than
              the thing you click. Now outlined and boxed, with the counter carried in a
              chip between them, so the group reads as one control. Disabled ends are
              still visible but plainly inert. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <button
              onClick={() => setCurrentQuestionIndex(Math.max(0, currentQuestionIndex - 1))}
              disabled={currentQuestionIndex === 0}
              style={{
                padding: '10px 20px',
                fontSize: '15px',
                fontWeight: '600',
                color: currentQuestionIndex === 0 ? '#b0b0b0' : '#1976d2',
                backgroundColor: currentQuestionIndex === 0 ? '#f5f5f5' : 'white',
                border: `2px solid ${currentQuestionIndex === 0 ? '#e0e0e0' : '#1976d2'}`,
                borderRadius: 0,
                cursor: currentQuestionIndex === 0 ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => {
                if (currentQuestionIndex !== 0) e.currentTarget.style.backgroundColor = '#e3f2fd';
              }}
              onMouseOut={(e) => {
                if (currentQuestionIndex !== 0) e.currentTarget.style.backgroundColor = 'white';
              }}
            >
              ← Previous
            </button>
            <span style={{
              fontSize: '15px',
              fontWeight: '700',
              color: '#333',
              backgroundColor: '#f0f0f0',
              padding: '8px 16px',
              borderRadius: 0,
              minWidth: '72px',
              textAlign: 'center',
              fontVariantNumeric: 'tabular-nums'
            }}>
              {currentQuestionIndex + 1} / {getFilteredQuestions().length}
            </span>
            <button
              onClick={() => setCurrentQuestionIndex(Math.min(getFilteredQuestions().length - 1, currentQuestionIndex + 1))}
              disabled={currentQuestionIndex >= getFilteredQuestions().length - 1}
              style={{
                padding: '10px 20px',
                fontSize: '15px',
                fontWeight: '600',
                color: currentQuestionIndex >= getFilteredQuestions().length - 1 ? '#b0b0b0' : 'white',
                backgroundColor: currentQuestionIndex >= getFilteredQuestions().length - 1 ? '#f5f5f5' : '#1976d2',
                border: `2px solid ${currentQuestionIndex >= getFilteredQuestions().length - 1 ? '#e0e0e0' : '#1976d2'}`,
                borderRadius: 0,
                cursor: currentQuestionIndex >= getFilteredQuestions().length - 1 ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => {
                if (currentQuestionIndex < getFilteredQuestions().length - 1) e.currentTarget.style.backgroundColor = '#1565c0';
              }}
              onMouseOut={(e) => {
                if (currentQuestionIndex < getFilteredQuestions().length - 1) e.currentTarget.style.backgroundColor = '#1976d2';
              }}
            >
              Next →
            </button>
          </div>

          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={async () => {
                await triggerRescoring();
              }}
              style={{
                padding: '10px 24px',
                fontSize: '14px',
                fontWeight: '500',
                color: '#666',
                backgroundColor: 'transparent',
                border: '1px solid #ddd',
                borderRadius: 0,
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
              onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              Save
            </button>
            <button
              onClick={async () => {
                await triggerRescoring();
                onClose();
              }}
              style={{
                padding: '10px 24px',
                fontSize: '14px',
                fontWeight: '500',
                color: 'white',
                backgroundColor: '#1976d2',
                border: 'none',
                borderRadius: 0,
                cursor: 'pointer',
                transition: 'background-color 0.2s'
              }}
              onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#1565c0'}
              onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#1976d2'}
            >
              Save & Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QAAssessmentModal;